import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ScrollRestoration from '@/components/feature/ScrollRestoration';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <SEOHead title="文章不存在 - 91看片网" description="未找到该文章" keywords="91看片网" canonicalUrl={`${siteConfig.url}/article/${id}`} noindex />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
      url: siteConfig.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.url}/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - ${siteConfig.name}`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${siteConfig.url}/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        ogImageWidth={800}
        ogImageHeight={450}
        articlePublishedTime={article.publishDate}
        structuredData={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollRestoration />
        <main className="max-w-4xl mx-auto px-4 md:px-6 pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <article>
            <div className="rounded-lg overflow-hidden aspect-[21/9] mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
                title={`${article.title} - 文章封面`}
              />
            </div>

            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                {article.category}
              </span>
              {article.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="px-2.5 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200 mb-8">
              <span className="flex items-center">
                <i className="ri-user-line mr-1.5"></i>
                {article.author}
              </span>
              <span className="flex items-center">
                <i className="ri-calendar-line mr-1.5"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center">
                <i className="ri-time-line mr-1.5"></i>
                {article.readTime} 阅读
              </span>
              <span className="flex items-center">
                <i className="ri-eye-line mr-1.5"></i>
                {article.viewCount.toLocaleString()} 次浏览
              </span>
              <span className="flex items-center">
                <i className="ri-star-fill text-accent-500 mr-1.5"></i>
                {article.rating} 分
              </span>
            </div>

            <div className="prose-custom text-foreground-800 leading-relaxed">
              <p className="text-base leading-relaxed mb-4">{article.excerpt}</p>
              <p className="text-base leading-relaxed mb-4">
                在当前的影视内容市场中，观众对高质量内容的需求日益增长。随着流媒体平台的崛起和国产影视作品的蓬勃发展，
                越来越多的优质内容不断涌现。本文将从多个维度深入分析这一现象，为读者提供全面的视角和深度解读。
              </p>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mt-8 mb-4">深度分析</h2>
              <p className="text-base leading-relaxed mb-4">
                从制作层面来看，近年来国产影视作品在技术水准和艺术表达上都有了显著提升。无论是特效制作、摄影构图，
                还是剧本创作、演员表演，都呈现出与国际接轨的专业水平。特别是在科幻、动画等类型上，
                国产作品正在形成自己独特的风格和语言。
              </p>
              <p className="text-base leading-relaxed mb-4">
                对于广大影视爱好者来说，这无疑是一个最好的时代。丰富的选择、多元的表达，让每一个观众都能找到属于自己的那份光影感动。
                我们期待未来会有更多优秀作品涌现，持续推动中国影视产业的高质量发展。
              </p>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mt-8 mb-4">未来展望</h2>
              <p className="text-base leading-relaxed mb-4">
                展望未来，AI技术、虚拟拍摄、实时渲染等新技术正在重塑影视制作的每一个环节。
                这些技术的应用不仅提升了制作效率，更为创作者提供了前所未有的表达空间。
                我们有理由相信，中国影视行业的明天会更加精彩。
              </p>
              <blockquote className="border-l-4 border-primary-400 pl-4 py-2 my-6 bg-background-100 rounded-r text-foreground-600 italic text-sm">
                光影之间，记录时代；画面之中，见证变迁。—— 91看片网编辑部
              </blockquote>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200 hover:border-primary-200 hover:text-primary-500 transition-colors duration-200 cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-12 pb-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}